import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { ChangeEvent, FC, useRef } from "react";
import { useAuthContext } from "../../../context/AuthContext";
import { useToast } from "../../../context/toast/ui";
import { ResursTreeApi } from "../../../services/modules/tree";
import { TResursTreeNode } from "../treeTypes";
import { checkUserTreeNodeType } from "../utils/checkUsetTreeNodeType";
import { handelAddUser } from "../utils/handelAddUser";
import { handleAddDevice } from "../utils/handleAddDevice";
import styles from "./resurs-tree-panel.module.css";

type TTreePanelActionEnum =
  | "add"
  | "run"
  | "quick"
  | "filter"
  | "search"
  | "fix_balance";

const treeToolMap: {
  [key in ResursTreeApi.treeName]: TTreePanelActionEnum[];
} = {
  device: ["add", "quick", "filter", "search"],
  user: ["add", "quick", "filter", "search"],
  balance: ["fix_balance", "search"],
  config: [],
  home: ["search"],
  operator: ["add", "search"],
  planner: ["run", "search"],
};

export const ResursTreePanel: FC<{
  tree: ResursTreeApi.treeName;
  createTreeButtonDisabled: boolean;
  lastSelect: {
    tree: string | null;
    objectIds: string[];
    nodes: TResursTreeNode[];
  };
  handleRunTask: (
    treeName: ResursTreeApi.treeName,
    node: TResursTreeNode
  ) => void;
  setShowCreateModal: (arg: boolean) => void;
  setTreeObjectListAvailableToAdd: (arg: any) => void;
  setShowQuick: (arg: boolean) => void;
  searchValue: string;
  setSearchValue: (term: string) => void;
  setSelectionFilterData: (arg: any) => void;
  setSelectionFilterModal: (arg: boolean) => void;
  performSearch: (afterCallback: () => void) => void;
}> = ({
  tree,
  createTreeButtonDisabled = false,
  setSearchValue,
  searchValue,
  handleRunTask,
  lastSelect,
  setSelectionFilterData,
  setSelectionFilterModal,
  setShowCreateModal,
  setShowQuick,
  setTreeObjectListAvailableToAdd,
  performSearch,
}) => {
  const targetTools = treeToolMap[tree];

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleFilter = async () => {
    const filters = await ResursTreeApi.getSelectionFilterByProperties({
      tree,
      objectId: lastSelect.objectIds,
    });

    setSelectionFilterData(filters);
    setSelectionFilterModal(true);
  };

  const { userRole } = useAuthContext();
  const { showMessage } = useToast();

  const handleFixBalance = async () => {
    if (userRole === "usr") {
      showMessage({
        caption: "Ошибка",
        message: "У вас нет прав!",
        tag: "add",
        timeOut: 3,
        type: "error",
      });

      return;
    }

    try {
      const result = await ResursTreeApi.execObjectCommand({
        tree: "balance",
        objectId: lastSelect.objectIds,
        command: "fix_balance",
        treeNeighbor: "device",
      });
      alert(JSON.stringify(result));
    } catch (e) {
      alert(JSON.stringify(e));
    }
  };

  const handleAdd = () => {
    if (userRole === "usr") {
      showMessage({
        caption: "Ошибка",
        message: "У вас нет прав!",
        tag: "add",
        timeOut: 3,
        type: "error",
      });

      return;
    }

    if (
      lastSelect.nodes[0]?.id === "root" &&
      userRole === "adm_mc" &&
      tree === "user"
    ) {
      showMessage({
        caption: "Ошибка",
        message: "У вас нет прав!",
        tag: "add",
        timeOut: 3,
        type: "error",
      });

      return;
    }

    switch (tree) {
      case "device": {
        console.log("selected devices: ", lastSelect);
        handleAddDevice(
          tree,
          lastSelect.nodes[0],
          setShowCreateModal,
          setTreeObjectListAvailableToAdd
        );
        break;
      }
      case "user": {
        if (!lastSelect.nodes[0]) return;
        if (lastSelect.tree !== "user") return;
        if (!checkUserTreeNodeType(lastSelect.nodes[0].type)) return;
        handelAddUser(
          tree,
          lastSelect.nodes[0],
          userRole,
          setShowCreateModal,
          setTreeObjectListAvailableToAdd
        );

        break;
      }

      case "operator": {
        handelAddUser(
          tree,
          lastSelect.nodes[0],
          userRole,
          setShowCreateModal,
          setTreeObjectListAvailableToAdd
        );

        break;
      }
    }
  };

  const searchInput = useRef<HTMLInputElement>(null);
  return (
    <div className={styles.resursTreePanel}>
      <div className={styles.addition}>
        {targetTools.includes("run") && (
          <button
            onClick={() => {
              if (userRole === "usr") {
                showMessage({
                  caption: "Ошибка",
                  message: "У вас нет прав!",
                  tag: "add",
                  timeOut: 3,
                  type: "error",
                });

                return;
              }

              handleRunTask(tree, lastSelect.nodes[0]);
            }}
            style={{
              width: 33,
              justifyContent: "center",
            }}
            className={styles.additionButton}
          >
            <i className="pi pi-play"></i>
          </button>
        )}
        {targetTools.includes("quick") && (
          <button
            disabled={createTreeButtonDisabled}
            style={{
              padding: 5,
              width: 33,
              justifyContent: "center",
            }}
            className={
              createTreeButtonDisabled
                ? styles.disabledAdditionButton
                : styles.additionButton
            }
            onClick={() => {
              if (userRole === "usr" || userRole === "adm_mc") {
                showMessage({
                  caption: "Ошибка",
                  message: "У вас нет прав!",
                  tag: "add",
                  timeOut: 3,
                  type: "error",
                });

                return;
              }
              setShowQuick(true);
            }}
          >
            <i className="pi pi-home"></i>
          </button>
        )}
        {targetTools.includes("add") && (
          <button className={styles.additionButton} onClick={handleAdd}>
            <div>Добавить</div>
            <i className="pi pi-plus-circle"></i>
          </button>
        )}
      </div>
      <div className={styles.searchFilter}>
        {targetTools.includes("fix_balance") && (
          <button className={styles.filterButton} onClick={handleFixBalance}>
            <i className="pi pi-lock"></i>
          </button>
        )}
        {targetTools.includes("search") && (
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search"> </InputIcon>
            <InputText
              ref={searchInput}
              value={searchValue}
              //autoFocus
              onChange={handleSearch}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  // Проверяем, если нажата клавиша Enter
                  performSearch(() => {
                    setTimeout(() => {
                      if (!searchInput.current) return;
                      searchInput.current.focus({ preventScroll: true });
                    }, 100);
                  });
                }
              }}
              placeholder="Поиск"
            />
          </IconField>
        )}

        {targetTools.includes("filter") && (
          <button className={styles.filterButton} onClick={handleFilter}>
            <i className="pi pi-filter"></i>
          </button>
        )}
      </div>
    </div>
  );
};
